<template>
  <div class="flex" :class="[vertical ? 'flex-row flex-wrap' : 'flex-col']">
    <div
      class="flex justify-between items-center px-3 h-6.5 py-1 border-b border-neutral-secondary"
      :class="[vertical ? 'w-1/2' : 'w-full']"
    >
      <NText weight="medium" variant="caption2">
        {{ t('symbol.trade_volume') }}
      </NText>

      <NPopper offset-y="2" hover>
        <NText>
          <NumberFormatter
            #="{ number }"
            :value="symbol.totalNumberOfSharesTraded"
            round
          >
            <NText weight="bold" variant="caption2">
              {{ number }}
            </NText>
          </NumberFormatter>
        </NText>

        <template #content>
          <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
            <NumberFormatter
              #="{ number }"
              :value="symbol.totalNumberOfSharesTraded"
            >
              <NText
                weight="bold"
                variant="caption2"
                class="text-neutral-quaternary"
              >
                {{ number }}
              </NText>
            </NumberFormatter>
          </div>
        </template>
      </NPopper>
    </div>

    <div
      class="flex justify-between items-center px-3 h-6.5 py-1 border-b border-neutral-secondary"
      :class="[vertical ? 'w-1/2' : 'w-full']"
    >
      <NText weight="medium" variant="caption2">
        {{ t('symbol.base_volume') }}
      </NText>

      <NumberFormatter #="{ number }" :value="symbol.baseQuantity" round>
        <NText weight="bold" variant="caption2">
          {{ number }}
        </NText>
      </NumberFormatter>
    </div>

    <div
      class="flex justify-between items-center px-3 h-6.5 border-b border-t border-neutral-secondary"
      :class="[vertical ? 'w-1/2' : 'w-full']"
    >
      <div class="inline-flex items-center">
        <NText weight="medium" variant="caption2">
          {{ t('symbol.trade_value') }}
        </NText>

        <NText
          weight="medium"
          variant="caption2"
          class="text-neutral-secondary mr-1"
        >
          {{ t('misc.rial') }}
        </NText>
      </div>

      <NPopper offset-y="2" hover>
        <NText>
          <NumberFormatter
            #="{ number }"
            notation="compact"
            minimum-fraction-digits="2"
            maximum-fraction-digits="2"
            :value="symbol.totalTradeValue"
          >
            <NText weight="bold" variant="caption2">
              {{ number }}
            </NText>
          </NumberFormatter>
        </NText>

        <template #content>
          <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
            <NumberFormatter #="{ number }" :value="symbol.totalTradeValue">
              <NText
                weight="bold"
                variant="caption2"
                class="text-neutral-quaternary"
              >
                {{ number }}
              </NText>
            </NumberFormatter>
          </div>
        </template>
      </NPopper>
    </div>

    <div
      v-if="!VALID_SECTOR_INVESTMENT_FUNDS.includes(symbol.sectorCode ?? '')"
      class="flex justify-between items-center px-3 py-1 border-b border-neutral-secondary"
      :class="[vertical ? 'w-1/2' : 'w-full']"
    >
      <div class="inline-flex items-center">
        <NText weight="medium" variant="caption2">
          {{ t('symbol.market_value') }}
        </NText>

        <NText
          weight="medium"
          variant="caption2"
          class="text-neutral-secondary mr-1"
        >
          {{ t('misc.rial') }}
        </NText>
      </div>

      <NPopper offset-y="2" hover>
        <NumberFormatter
          #="{ number }"
          notation="compact"
          maximum-fraction-digits="2"
          minimum-fraction-digits="2"
          :value="symbol.instrumentMarketValue"
          round
        >
          <NText weight="bold" variant="caption2">
            {{ number }}
          </NText>
        </NumberFormatter>

        <template #content>
          <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
            <NumberFormatter
              #="{ number }"
              :value="symbol.instrumentMarketValue"
            >
              <NText
                weight="bold"
                variant="caption2"
                class="text-neutral-quaternary"
              >
                {{ number }}
              </NText>
            </NumberFormatter>
          </div>
        </template>
      </NPopper>
    </div>

    <div
      class="flex justify-between items-center px-3 h-6.5 py-1 border-b border-neutral-secondary"
      :class="[vertical ? 'w-1/2' : 'w-full']"
    >
      <NText weight="medium" variant="caption2">
        {{ t('symbol.last_trade_time') }}
      </NText>

      <NText weight="bold" variant="caption2" dir="ltr">
        {{ lastTradeTime }}
      </NText>
    </div>

    <template
      v-if="
        VALID_INSTRUMENT_TYPES_STOCK_INFORMATION.includes(
          symbol.instrumentTypeCode ?? ''
        )
      "
    >
      <div
        class="flex justify-between items-center px-3 h-6.5 py-1 border-b border-neutral-secondary"
        :class="[vertical ? 'w-1/2' : 'w-full']"
      >
        <div class="inline-flex items-center">
          <NText weight="medium" variant="caption2">
            {{ t('symbol.earningsPerShare') }}
          </NText>

          <NText
            weight="medium"
            variant="caption2"
            class="text-neutral-secondary mr-1"
          >
            {{ t('misc.rial') }}
          </NText>
        </div>

        <NumberFormatter #="{ number }" :value="symbol.eps">
          <NText weight="bold" variant="caption2" dir="ltr">
            {{ number }}
          </NText>
        </NumberFormatter>
      </div>

      <div
        class="flex justify-between items-center px-3 h-6.5 py-1 border-b border-neutral-secondary"
        :class="[vertical ? 'w-1/2' : 'w-full']"
      >
        <NText weight="medium" variant="caption2">
          {{ t('symbol.priceToEarnings') }}
        </NText>

        <NumberFormatter
          #="{ number }"
          :value="symbol.pricePerEarning"
          :maximum-fraction-digits="2"
        >
          <NText weight="bold" variant="caption2" dir="ltr">
            {{ number }}
          </NText>
        </NumberFormatter>
      </div>

      <div
        class="flex justify-between items-center px-3 h-6.5 py-1 border-b border-neutral-secondary"
        :class="[vertical ? 'w-1/2' : 'w-full']"
      >
        <NText weight="medium" variant="caption2">
          {{ t('symbol.groupPriceToEarnings') }}
        </NText>

        <NumberFormatter
          #="{ number }"
          :value="symbol.pricePerEarningGroup"
          :maximum-fraction-digits="2"
        >
          <NText weight="bold" variant="caption2" dir="ltr">
            {{ number }}
          </NText>
        </NumberFormatter>
      </div>
    </template>

    <template
      v-if="VALID_SECTOR_INVESTMENT_FUNDS.includes(symbol.sectorCode ?? '')"
    >
      <div
        class="flex justify-between items-center px-3 h-6.5 py-1 border-b border-neutral-secondary"
        :class="[vertical ? 'w-1/2' : 'w-full']"
      >
        <div class="inline-flex items-center">
          <NText weight="medium" variant="caption2">
            {{ t('symbol.redemptionNav') }}
          </NText>

          <NText
            weight="medium"
            variant="caption2"
            class="text-neutral-secondary mr-1"
          >
            {{ t('misc.rial') }}
          </NText>
        </div>

        <NumberFormatter #="{ number }" :value="symbol.priceOfRedemptionNav">
          <NText weight="bold" variant="caption2">
            {{ number }}
          </NText>
        </NumberFormatter>
      </div>

      <div
        v-if="vertical"
        class="flex justify-between items-center px-3 h-6.5 py-1 border-b border-neutral-secondary w-1/2"
      >
        <div class="inline-flex items-center relative">
          <NText weight="medium" variant="caption2">
            {{ t('symbol.bubble.title') }}
          </NText>

          <NegarInfoWithBackgroundIcon
            class="cursor-pointer text-neutral-secondary"
            @click="openInstrumentFundsBubbleInfoDialog"
          />

          <NegarNotifIcon
            size="10"
            color="orange"
            class="absolute left-3 top-0"
          />
        </div>

        <NumberFormatter
          #="{ number }"
          :value="bubblePercent / 100"
          number-style="percent"
          maximum-fraction-digits="2"
          sign-display="always"
        >
          <NText weight="bold" variant="caption2" dir="ltr">
            {{ number }}
          </NText>
        </NumberFormatter>
      </div>

      <div
        class="flex justify-between items-center px-3 h-6.5 py-1 border-b border-neutral-secondary"
        :class="[vertical ? 'w-1/2' : 'w-full']"
      >
        <NText
          variant="caption2"
          weight="medium"
          class="text-neutral-secondary-press"
        >
          {{ t('symbol.noticeTime') }}
        </NText>

        <NText variant="caption2" weight="bold" dir="ltr">
          {{ navFullDateOfEvent }}
        </NText>
      </div>
    </template>

    <div
      v-if="openOrdersQuantity.buy"
      class="flex justify-between items-center px-3 h-6.5 py-1 border-b border-neutral-secondary"
      :class="[vertical ? 'w-1/2' : 'w-full']"
    >
      <NText weight="medium" variant="caption2">
        {{ t('symbol.openBuyOrders') }}
      </NText>

      <NumberFormatter #="{ number }" :value="openOrdersQuantity.buy">
        <NText weight="bold" variant="caption2">
          {{ openOrdersQuantity.buy }}
        </NText>
      </NumberFormatter>
    </div>

    <div
      v-if="totalTradedQuantity.buy"
      class="flex justify-between items-center px-3 h-6.5 py-1 border-b border-neutral-secondary"
      :class="[vertical ? 'w-1/2' : 'w-full']"
    >
      <NText weight="medium" variant="caption2">
        {{ t('symbol.tradedBuy') }}
      </NText>

      <NumberFormatter #="{ number }" :value="totalTradedQuantity.buy">
        <NText weight="bold" variant="caption2">
          {{ number }}
        </NText>
      </NumberFormatter>
    </div>

    <div
      v-if="openOrdersQuantity.sell"
      class="flex justify-between items-center px-3 h-6.5 py-1 border-b border-neutral-secondary"
      :class="[vertical ? 'w-1/2' : 'w-full']"
    >
      <NText weight="medium" variant="caption2">
        {{ t('symbol.openSellOrders') }}
      </NText>

      <NumberFormatter #="{ number }" :value="openOrdersQuantity.sell">
        <NText weight="bold" variant="caption2">
          {{ number }}
        </NText>
      </NumberFormatter>
    </div>

    <div
      v-if="totalTradedQuantity.sell"
      class="flex justify-between items-center px-3 h-6.5 py-1 border-b border-neutral-secondary"
      :class="[vertical ? 'w-1/2' : 'w-full']"
    >
      <NText weight="medium" variant="caption2">
        {{ t('symbol.tradedSell') }}
      </NText>

      <NumberFormatter #="{ number }" :value="totalTradedQuantity.sell">
        <NText weight="bold" variant="caption2">
          {{ number }}
        </NText>
      </NumberFormatter>
    </div>

    <div
      v-if="portfolioDetail && portfolioDetail.quantity > 0"
      class="flex justify-between items-center px-3 h-6.5 py-1"
    >
      <NText
        weight="medium"
        variant="caption2"
        :class="{
          'ml-1': vertical,
        }"
      >
        {{ t('symbol.net_assets') }} ({{ t('misc.return') }})
      </NText>

      <span class="inline-flex items-center">
        <NumberFormatter
          #="{ number }"
          :value="portfolioDetail.percentageValue / 100"
          number-style="percent"
          maximum-fraction-digits="2"
          sign-display="always"
        >
          <NText
            class="align-middle ms-1"
            dir="ltr"
            :class="{
              'text-brand-primary': portfolioDetail.percentageValue > 0,
              'text-critical-primary': portfolioDetail.percentageValue < 0,
            }"
            variant="caption2"
            weight="bold"
          >
            ({{ number }})
          </NText>
        </NumberFormatter>

        <NumberFormatter #="{ number }" :value="portfolioDetail.quantity">
          <NText weight="bold" variant="caption2">
            {{ number }}
          </NText>
        </NumberFormatter>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Instrument } from '@libs/api-reference';
import { convertFaDigitsToEn } from '@libs/utils';
import {
  calcBubbleOfInvestmentFunds,
  VALID_INSTRUMENT_TYPES_STOCK_INFORMATION,
  VALID_SECTOR_INVESTMENT_FUNDS,
} from '@core/symbol';
import { isOpenOrder, ORDER_SIDES, useLiveOrdersQuery } from '@core/order';
import { NegarInfoWithBackgroundIcon, NegarNotifIcon } from '@negar/icons';

import { useSymbolDialogs } from '@/modules/symbol';

import NumberFormatter from '@/components/Number.vue';

type SymbolTradeInfoProps = {
  symbol: Instrument;
  portfolioDetail: { quantity: number; percentageValue: number } | null;
  vertical: boolean;
};
const props = defineProps<SymbolTradeInfoProps>();

const { d, t } = useI18n();

const { orders } = useLiveOrdersQuery();

const { openInstrumentFundsBubbleInfoDialog } = useSymbolDialogs();

const lastTradeTime = computed(() =>
  props.symbol.lastTradeDate
    ? `${convertFaDigitsToEn(d(props.symbol.lastTradeDate))} ${d(
        props.symbol.lastTradeDate,
        'shortTime',
        'en'
      )}`
    : ''
);

const navFullDateOfEvent = computed(() =>
  props.symbol.navDateOfEvent
    ? `${convertFaDigitsToEn(d(props.symbol.navDateOfEvent))} ${d(
        props.symbol.navDateOfEvent,
        'shortTime',
        'en'
      )}`
    : ''
);

const openOrdersQuantity = computed<{ buy: number; sell: number }>(() => {
  return orders.value.reduce<{ buy: number; sell: number }>(
    (prevOrders, order) => {
      if (
        isOpenOrder(order) &&
        order.instrumentIdentification === props.symbol.isin
      ) {
        order.orderSideId === ORDER_SIDES.buy
          ? (prevOrders.buy += order.quantity - order.executedQuantity)
          : (prevOrders.sell += order.quantity - order.executedQuantity);
      }

      return prevOrders;
    },
    {
      buy: 0,
      sell: 0,
    }
  );
});

const totalTradedQuantity = computed<{ buy: number; sell: number }>(() => {
  return orders.value.reduce<{ buy: number; sell: number }>(
    (prevOrders, order) => {
      if (order.instrumentIdentification === props.symbol.isin) {
        order.orderSideId === ORDER_SIDES.buy
          ? (prevOrders.buy += order.executedQuantity)
          : (prevOrders.sell += order.executedQuantity);
      }

      return prevOrders;
    },
    {
      buy: 0,
      sell: 0,
    }
  );
});

const bubblePercent = computed(() =>
  props.symbol.sectorCode &&
  VALID_SECTOR_INVESTMENT_FUNDS.includes(props.symbol.sectorCode)
    ? calcBubbleOfInvestmentFunds({
        lastTradePrice: props.symbol.lastTradePrice,
        priceOfNav: props.symbol.priceOfRedemptionNav,
      })
    : 0
);
</script>
